/**
 * @file Represents the main app page which combines various UI components.
 * It houses the central logic for rendering different views based on screen size.
 */
import ProAppPage from './ProAppPage';
import BasicAppPage from './BasicAppPage';
import FilterStatesContextWrapper from '../../components/contexts/filterStates';
import { useAuth0 } from '@auth0/auth0-react';

/** @component
 * Page for /app
 *
 * The page rendering is dynamic, it renders a different page based on your subscription.
 *
 * As a rule of thumb, when writing code for extending this pattern:
 * - If applies to Basic only, the code should be added to the basic page.
 * - If applies to Pro only, the code should be added to the pro page.
 * - If applies to both, the code should be encapsulated in a shared
 *      component between the pages.
 *
 * The idea with this is to segregate the responsibility for pro and basic
 * parts of our application, so that:
 * - There is only one check in the app for subscription type. This is a fairly
 *      important distinction to always get right and safer as a single
 *      operation on the parent component as done here. There's only one
 *      place it can go wrong...
 * - There is still an ability to share logic between the two pages. We don't
 * want to duplicate functionality that is always as standard in the page.
 *
 * @returns {JSX.Element}
 * @constructor
 */
const AppPage = () => {
    const { user } = useAuth0();
    const subscriptionType = user?.subscription_type ?? 'unauthorized';

    if (subscriptionType === 'pro' || subscriptionType === 'advantage') {
        return (
            <FilterStatesContextWrapper>
                <ProAppPage />
            </FilterStatesContextWrapper>
        );
    } else if (
        subscriptionType === 'basic' ||
        subscriptionType === 'unauthorized'
    ) {
        return (
            <FilterStatesContextWrapper>
                <BasicAppPage />
            </FilterStatesContextWrapper>
        );
    }
};

export default AppPage;
